
import {Component, Vue, Watch} from "vue-property-decorator";
import {MdbsCore} from "@/api/tsclient";
import {currentUserModule, studyDefinitionAdminModule, studyDefinitionModule} from "@/store";
import {startDownload} from "@/auxillary/export.helper";
import {translate} from "@/auxillary/translation";
import {DateTime} from "luxon";
import DateInputMenu from "@/components/common/DateInputMenu.vue";
import Rights = MdbsCore.Rights;
import adminGuides from "@/assets/documents/adminGuides";

@Component({
  components: {DateInputMenu}
})
export default class StudyDefinitionAdmin extends Vue {
  importFile: File | null = null;
  testRun = true;
  ethicDate: Date | null = null;
  goLiveDate: Date | null = null;
  showDatesUpdated = false;
  importing = false;
  updatingDates = false;
  tab = 0;
  resultTab = 0;
  historyTab = 0;
  showDefinitionDetails = false;
  defFilterObjectType: string | null = null;
  defFilterChange: MdbsCore.DefinitionChangeType | null = null;
  showCheckDetails = false;
  checkFilterObjectType: string | null = null;
  checkFilterCategory: string | null = null;

  @Watch("studyDefinition", {immediate: true})
  onStudyDefinitionChange(def: MdbsCore.StudyDefinitionDto | null) {
    this.ethicDate = this.normalizeDate(def?.ethicDate);
    this.goLiveDate = this.normalizeDate(def?.goLiveDate);
  }

  // Load the history the first time the "Verlauf" tab is opened (tab index varies with the user's rights).
  onVerlaufOpen() {
    this.loadHistoryForCurrentSubTab();
  }

  // Lazy-load the sub-tab's data on first view (Import = 0, Export = 1).
  @Watch("historyTab")
  onHistoryTabChanged() {
    this.loadHistoryForCurrentSubTab();
  }

  private loadHistoryForCurrentSubTab() {
    if (this.historyTab === 0) {
      if (this.canSeeHistory && this.history.length === 0) studyDefinitionAdminModule.loadHistory();
    } else if (this.historyTab === 1) {
      if (this.canSeeExportHistory && this.exportHistory.length === 0) studyDefinitionAdminModule.loadExportHistory();
    } else {
      if (this.canSeeExportHistory && this.downloadHistory.length === 0) studyDefinitionAdminModule.loadDownloadHistory();
    }
  }

  // A fresh import may show a different set of result tabs (empty ones are hidden), so jump back to the first one.
  @Watch("report")
  onReportChange() {
    this.resultTab = 0;
    this.showDefinitionDetails = false;
    this.showCheckDetails = false;
  }

  get history() {
    return studyDefinitionAdminModule.history;
  }

  get canSeeHistory() {
    return currentUserModule.hasRight(Rights.StudyDefinitionHistoryGetEntriesCmd);
  }

  get exportHistory() {
    return studyDefinitionAdminModule.exportHistory;
  }

  get downloadHistory() {
    return studyDefinitionAdminModule.downloadHistory;
  }

  get canSeeExportHistory() {
    return currentUserModule.hasRight(Rights.AuditLogGetEntriesCmd);
  }

  exportParam(entry: MdbsCore.AuditLogEntryDto, key: string): any {
    return entry && entry.parameters ? entry.parameters[key] : undefined;
  }

  exportSuccess(entry: MdbsCore.AuditLogEntryDto): boolean {
    const tags: any = entry?.tags;
    return !tags || tags.Success !== false;
  }

  exportArt(entry: MdbsCore.AuditLogEntryDto): string {
    return entry?.identifier === "StudyDefinitionSeederExport" ? "Seeder-Export" : "Export";
  }

  param(entry: MdbsCore.StudyDefinitionHistoryEntryDto, key: string): any {
    return entry && entry.parameters ? entry.parameters[key] : undefined;
  }

  isSuccess(entry: MdbsCore.StudyDefinitionHistoryEntryDto): boolean {
    return !!(entry && entry.result && entry.result.Success);
  }

  isSkipped(entry: MdbsCore.StudyDefinitionHistoryEntryDto): boolean {
    return this.param(entry, 'Outcome') === 'Skipped';
  }

  outcomeColor(entry: MdbsCore.StudyDefinitionHistoryEntryDto): string {
    if (this.isSkipped(entry)) return 'grey';
    return this.isSuccess(entry) ? 'success' : 'error';
  }

  outcomeLabel(entry: MdbsCore.StudyDefinitionHistoryEntryDto): string {
    const outcome = this.param(entry, 'Outcome');
    if (outcome === 'Skipped') return 'Übersprungen';
    if (outcome === 'Success') return 'Erfolg';
    if (outcome === 'Failed') return 'Fehler';
    if (outcome) return outcome;
    return this.isSuccess(entry) ? 'Erfolg' : 'Fehler';
  }

  formatDateTime(date?: Date): string {
    if (!date) return "—";
    return DateTime.fromJSDate(date).toFormat("dd.MM.yyyy HH:mm");
  }

  async refreshHistory() {
    await studyDefinitionAdminModule.loadHistory();
  }

  async refreshCurrentHistory() {
    if (this.historyTab === 0) await studyDefinitionAdminModule.loadHistory();
    else if (this.historyTab === 1) await studyDefinitionAdminModule.loadExportHistory();
    else await studyDefinitionAdminModule.loadDownloadHistory();
  }

  get currentHistoryHasEntries() {
    if (this.historyTab === 0) return this.history.length > 0;
    if (this.historyTab === 1) return this.exportHistory.length > 0;
    return this.downloadHistory.length > 0;
  }

  async downloadCurrentHistory() {
    let response;
    if (this.historyTab === 0) response = await studyDefinitionAdminModule.exportImportHistory();
    else if (this.historyTab === 1) response = await studyDefinitionAdminModule.exportExportHistory();
    else response = await studyDefinitionAdminModule.exportDownloadHistory();
    if (response) startDownload(response.data, response.fileName ?? "audit-log-export.csv");
  }

  get canDownloadReport() {
    return currentUserModule.hasRight(Rights.StudyDefinitionHistoryGetFileCmd);
  }

  async downloadReport(fileName: string) {
    if (!fileName) return;
    const response = await studyDefinitionAdminModule.downloadReportFile(fileName);
    if (response) {
      startDownload(response.data, response.fileName ?? fileName);
      // A download writes a new audit entry -> keep the download history in sync.
      if (this.canSeeExportHistory) await studyDefinitionAdminModule.loadDownloadHistory();
    }
  }

  private normalizeDate(date?: Date): Date | null {
    // Backend liefert leere Datumsangaben als DateTime.MinValue (0001-01-01)
    if (!date || date.getFullYear() <= 1) return null;
    return date;
  }

  get studyDefinition() {
    return studyDefinitionModule.studyDefinition ?? null;
  }

  get hasStudyDefinition(): boolean {
    return !!this.studyDefinition?.id;
  }

  get studyDefinitionName(): string {
    const key = this.studyDefinition?.displayKey;
    return key ? translate(key) : (this.studyDefinition?.identifier ?? "");
  }

  get studyDefinitionVersion(): string {
    return this.studyDefinition?.version ?? "";
  }

  get canCheckSeedVersion(): boolean {
    return currentUserModule.hasRight(Rights.SeededStudyDefinitionVersionsGetCmd);
  }

  // The seeded (deployment/file) version for the currently loaded study definition, if available.
  get seededFileVersion(): string | null {
    const id = this.studyDefinition?.identifier;
    if (!id) return null;
    const match = studyDefinitionAdminModule.seededVersions.find(
        v => (v.identifier || "").toLowerCase() === id.toLowerCase());
    return match?.fileVersion ?? null;
  }

  // Warns when the deployed StudyDefinition.json and the DB differ, in either direction.
  get versionWarning(): { type: string, message: string } | null {
    const db = this.studyDefinitionVersion;
    const file = this.seededFileVersion;
    if (!this.hasStudyDefinition || !db || !file) return null;
    const cmp = this.compareVersions(file, db);
    if (cmp === 0) return null;
    if (cmp > 0) {
      return {
        type: "warning",
        message: `Die Datenbank-Version (v${db}) ist älter als die im Programm ausgelieferte Version (v${file}). `
            + `Bitte die Study Definition über den Import aktualisieren.`
      };
    }
    return {
      type: "info",
      message: `Die im Programm ausgelieferte Version (v${file}) ist älter als die Datenbank-Version (v${db}). `
          + `Die Datenbank ist neuer als der Programmstand.`
    };
  }

  // Numeric, segment-wise version compare. Returns >0 if a>b, <0 if a<b, 0 if equal.
  private compareVersions(a: string, b: string): number {
    const pa = (a || "").split(".").map(n => parseInt(n, 10) || 0);
    const pb = (b || "").split(".").map(n => parseInt(n, 10) || 0);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
      const d = (pa[i] || 0) - (pb[i] || 0);
      if (d !== 0) return d > 0 ? 1 : -1;
    }
    return 0;
  }

  async mounted() {
    if (this.canCheckSeedVersion) await studyDefinitionAdminModule.loadSeededVersions();
  }

  get loading() {
    return studyDefinitionAdminModule.isLoading;
  }

  get report() {
    return studyDefinitionAdminModule.importReport;
  }

  get resultHeadline(): string {
    const r = this.report;
    if (!r) return "";
    if (r.skipped) return "Übersprungen – Version nicht neuer, keine Änderungen gespeichert.";
    if (r.success) return this.testRun ? "Testlauf erfolgreich – keine Änderungen gespeichert." : "Import erfolgreich.";
    return "Import fehlgeschlagen: " + (r.errorMessage || "unbekannter Fehler");
  }

  private get studyDefinitionId(): string {
    return studyDefinitionModule.studyDefinition?.id ?? "";
  }

  private get baseFileName(): string {
    const def = studyDefinitionModule.studyDefinition;
    return `${def?.identifier ?? 'StudyDefinition'}_${def?.version ?? ''}`;
  }

  // Real runtime/patient data that was soft-deleted, grouped by object type.
  get deletionSections() {
    const d = this.report?.dataImpact;
    if (!d) return [];
    const sections = [
      {label: "Gelöschte Visiten", entries: d.dataDeletedVisits ?? []},
      {label: "Gelöschte Instrumente", entries: d.dataDeletedInstruments ?? []},
      {label: "Gelöschte Instrumentfelder", entries: d.dataDeletedInstrumentFields ?? []},
      {label: "Gelöschte Visitenfelder", entries: d.dataDeletedVisitFields ?? []},
      {label: "Gelöschte Patientenfelder", entries: d.dataDeletedPatientFields ?? []},
      {label: "Gelöschte Verification Flags", entries: d.dataDeletedVerificationFlags ?? []},
      {label: "Gelöschte Queries", entries: d.dataDeletedQueries ?? []},
    ];
    return sections.filter(s => s.entries.length > 0);
  }

  // All deletions flattened into one list (category + affected count + identifier) for a single compact table.
  // Sorted consistently: Kategorie → Identifier → Anzahl.
  get deletionRows() {
    const rows: { category: string, count: number, identifier: string }[] = [];
    for (const section of this.deletionSections) {
      for (const e of section.entries) {
        rows.push({
          category: section.label.replace(/^Gelöschte\s+/, ""),
          count: (e as any).count ?? 0,
          identifier: (e as any).definitionIdentifier || (e as any).definitionId,
        });
      }
    }
    return rows.sort((a, b) =>
        a.category.localeCompare(b.category)
        || (a.identifier || "").localeCompare(b.identifier || "")
        || a.count - b.count);
  }

  // Runtime rows needing a follow-up migration (type-changed fields with data, backfill of new fields).
  // Sorted consistently: Objekt → Identifier → Grund → Anzahl.
  get migrationNeeded() {
    return (this.report?.dataImpact?.dataMigrationNeeded ?? [])
        .slice()
        .sort((a, b) =>
            (a.objectType || "").localeCompare(b.objectType || "")
            || (a.definitionIdentifier || a.definitionId || "").localeCompare(b.definitionIdentifier || b.definitionId || "")
            || this.migrationReason(a).localeCompare(this.migrationReason(b))
            || (a.affectedCount || 0) - (b.affectedCount || 0));
  }

  // Heuristic hints that existing data may need re-checking (rule / field-definition changes).
  get checksNeeded() {
    return this.report?.dataImpact?.dataChecksNeeded ?? [];
  }

  // Structural definition changelog (added/updated/deleted/restored definitions of all types).
  get definitionChanges() {
    return this.report?.dataImpact?.definitionChanges ?? [];
  }

  // Aggregated statistic per object type (added/updated/deleted/restored counts) — the readable overview.
  get definitionChangeSummary() {
    const map = new Map<string, { objectType: string; added: number; updated: number; deleted: number; restored: number; total: number }>();
    for (const c of this.definitionChanges) {
      const key = c.objectType || "—";
      const row = map.get(key) ?? {objectType: key, added: 0, updated: 0, deleted: 0, restored: 0, total: 0};
      switch (c.change) {
        case MdbsCore.DefinitionChangeType.Added: row.added++; break;
        case MdbsCore.DefinitionChangeType.Updated: row.updated++; break;
        case MdbsCore.DefinitionChangeType.Deleted: row.deleted++; break;
        case MdbsCore.DefinitionChangeType.Restored: row.restored++; break;
      }
      row.total++;
      map.set(key, row);
    }
    return Array.from(map.values()).sort((a, b) => a.objectType.localeCompare(b.objectType));
  }

  // Distinct object types present, for the filter dropdown.
  get defObjectTypes(): string[] {
    return Array.from(new Set(this.definitionChanges.map(c => c.objectType).filter(Boolean) as string[])).sort();
  }

  // DefinitionChangeType is a string enum, so it cannot be subtracted. Rank it explicitly
  // in the same order the filter dropdown uses; unknown values sort last.
  defChangeRank(change?: MdbsCore.DefinitionChangeType): number {
    switch (change) {
      case MdbsCore.DefinitionChangeType.Added: return 0;
      case MdbsCore.DefinitionChangeType.Updated: return 1;
      case MdbsCore.DefinitionChangeType.Deleted: return 2;
      case MdbsCore.DefinitionChangeType.Restored: return 3;
      default: return 4;
    }
  }

  // Definition changes sorted by object type (then change type, then identifier) and filtered.
  get sortedDefinitionChanges() {
    return this.definitionChanges
        .filter(c => !this.defFilterObjectType || c.objectType === this.defFilterObjectType)
        .filter(c => this.defFilterChange == null || c.change === this.defFilterChange)
        .slice()
        .sort((a, b) =>
            (a.objectType || "").localeCompare(b.objectType || "")
            || this.defChangeRank(a.change) - this.defChangeRank(b.change)
            || (a.identifier || a.id || "").localeCompare(b.identifier || b.id || ""));
  }

  // Options for the change-type filter (values actually present).
  get defChangeTypeOptions() {
    const present = new Set(this.definitionChanges.map(c => c.change));
    return [
      MdbsCore.DefinitionChangeType.Added,
      MdbsCore.DefinitionChangeType.Updated,
      MdbsCore.DefinitionChangeType.Deleted,
      MdbsCore.DefinitionChangeType.Restored,
    ].filter(t => present.has(t))
     .map(t => ({value: t, text: this.definitionChangeLabel(t)}));
  }

  get hasDetails() {
    return this.dataImpactCount > 0
        || this.validationEntries.length > 0
        || this.definitionChangesCount > 0;
  }

  // Consequences for real runtime/patient data.
  get dataImpactCount() {
    return this.deletionSections.reduce((n, s) => n + s.entries.length, 0)
        + this.migrationNeeded.length
        + this.checksNeeded.length;
  }

  // Structural definition changelog (no data impact).
  get definitionChangesCount() {
    return this.definitionChanges.length;
  }

  get validationEntries() {
    return this.report?.validationReport?.entries ?? [];
  }

  validationColor(type?: MdbsCore.StudyDefinitionValidationReportType): string {
    switch (type) {
      case MdbsCore.StudyDefinitionValidationReportType.ERROR:
        return "error";
      case MdbsCore.StudyDefinitionValidationReportType.WARNING:
        return "warning";
      case MdbsCore.StudyDefinitionValidationReportType.INFO:
        return "info";
      default:
        return "grey";
    }
  }

  definitionChangeColor(change?: MdbsCore.DefinitionChangeType): string {
    switch (change) {
      case MdbsCore.DefinitionChangeType.Added:
        return "success";
      case MdbsCore.DefinitionChangeType.Updated:
        return "info";
      case MdbsCore.DefinitionChangeType.Deleted:
        return "error";
      case MdbsCore.DefinitionChangeType.Restored:
        return "warning";
      default:
        return "grey";
    }
  }

  definitionChangeLabel(change?: MdbsCore.DefinitionChangeType): string {
    switch (change) {
      case MdbsCore.DefinitionChangeType.Added:
        return "Neu";
      case MdbsCore.DefinitionChangeType.Updated:
        return "Geändert";
      case MdbsCore.DefinitionChangeType.Deleted:
        return "Gelöscht";
      case MdbsCore.DefinitionChangeType.Restored:
        return "Wiederhergestellt";
      default:
        return String(change ?? "—");
    }
  }

  // Human reason for a "Migration nötig" row: a type change shows the transition, everything else is a new field.
  migrationReason(m: { oldType?: string; newType?: string }): string {
    if (m.oldType || m.newType) {
      return `Typänderung: ${m.oldType || "—"} → ${m.newType || "—"}`;
    }
    return "Neu";
  }

  // Maps a raw check trigger (e.g. "RequiredFieldRuleAdded", "FieldDefinitionChanged") to a readable German reason.
  // "Added" reads as "Neue <X>regel" to match the "Neu" wording used in "Migration nötig".
  checkReasonLabel(trigger?: string): string {
    if (!trigger) return "—";
    if (trigger === "FieldDefinitionChanged") return "Felddefinition geändert";

    const match = /^(Visibility|RequiredField|EditCheck|Editability)Rule(Added|Changed|Deleted|Reactivated)$/.exec(trigger);
    if (!match) return trigger;

    const rule: Record<string, string> = {
      Visibility: "Sichtbarkeitsregel",
      RequiredField: "Pflichtfeldregel",
      EditCheck: "Prüfregel",
      Editability: "Editierbarkeitsregel",
    };
    if (match[2] === "Added") return `Neue ${rule[match[1]]}`;

    const action: Record<string, string> = {
      Changed: "geändert",
      Deleted: "entfernt",
      Reactivated: "reaktiviert",
    };
    return `${rule[match[1]]} ${action[match[2]]}`;
  }

  // Category of a check reason, used for the statistic and the detail filter.
  checkCategory(trigger?: string): string {
    const match = /Rule(Added|Changed|Deleted|Reactivated)$/.exec(trigger || "");
    switch (match?.[1]) {
      case "Added": return "Neu";
      case "Deleted": return "Entfernt";
      case "Reactivated": return "Reaktiviert";
      default: return "Geändert"; // Changed / FieldDefinitionChanged
    }
  }

  // Dezente Farbe je Kategorie: neu/reaktiviert → grün, entfernt → rot, geändert → blau.
  checkReasonColorClass(trigger?: string): string {
    switch (this.checkCategory(trigger)) {
      case "Neu":
      case "Reaktiviert": return "green--text text--darken-2";
      case "Entfernt": return "red--text text--darken-1";
      default: return "blue--text text--darken-1"; // Geändert
    }
  }

  // Statistic per object type (Neu / Geändert / Entfernt / Reaktiviert), mirroring the definition-change summary.
  get checksSummary() {
    const map = new Map<string, { objectType: string; neu: number; geaendert: number; entfernt: number; reaktiviert: number; total: number }>();
    for (const c of this.checksNeeded) {
      const key = c.objectType || "—";
      const row = map.get(key) ?? {objectType: key, neu: 0, geaendert: 0, entfernt: 0, reaktiviert: 0, total: 0};
      switch (this.checkCategory(c.trigger)) {
        case "Neu": row.neu++; break;
        case "Entfernt": row.entfernt++; break;
        case "Reaktiviert": row.reaktiviert++; break;
        default: row.geaendert++; break;
      }
      row.total++;
      map.set(key, row);
    }
    return Array.from(map.values()).sort((a, b) => a.objectType.localeCompare(b.objectType));
  }

  // Distinct object types among checks, for the filter dropdown.
  get checkObjectTypes(): string[] {
    return Array.from(new Set(this.checksNeeded.map(c => c.objectType).filter(Boolean) as string[])).sort();
  }

  // Categories actually present, for the category filter.
  get checkCategoryOptions(): string[] {
    return ["Neu", "Geändert", "Entfernt", "Reaktiviert"]
        .filter(cat => this.checksNeeded.some(c => this.checkCategory(c.trigger) === cat));
  }

  // Checks filtered by the two dropdowns and sorted by object type then reason.
  get filteredChecks() {
    return this.checksNeeded
        .filter(c => !this.checkFilterObjectType || c.objectType === this.checkFilterObjectType)
        .filter(c => !this.checkFilterCategory || this.checkCategory(c.trigger) === this.checkFilterCategory)
        .slice()
        .sort((a, b) =>
            (a.objectType || "").localeCompare(b.objectType || "")
            || this.checkReasonLabel(a.trigger).localeCompare(this.checkReasonLabel(b.trigger))
            || (a.definitionIdentifier || a.definitionId || "").localeCompare(b.definitionIdentifier || b.definitionId || ""));
  }

  get canExport() {
    return currentUserModule.hasRight(Rights.ExportStudyDefinitionCmd);
  }

  get canExportSeeder() {
    return currentUserModule.hasRight(Rights.ExportStudySeederDefinitionCmd);
  }

  get canImport() {
    return currentUserModule.hasRight(Rights.ImportStudyDefinitionCmd);
  }

  get canUpdateDates() {
    return currentUserModule.hasRight(Rights.UpdateStudyDefinitionDatesCmd);
  }

  async exportDefinition() {
    const response = await studyDefinitionAdminModule.exportDefinition(this.studyDefinitionId);
    if (response) {
      startDownload(response.data, response.fileName ?? `${this.baseFileName}_StudyDefinition.zip`);
    }
  }

  async exportSeeder() {
    const response = await studyDefinitionAdminModule.exportSeeder(this.studyDefinitionId);
    if (response) {
      startDownload(response.data, response.fileName ?? `${this.baseFileName}_StudySeeder.zip`);
    }
  }

  async runImport() {
    if (!this.importFile) return;
    this.importing = true;
    try {
      await studyDefinitionAdminModule.importDefinition({testRun: this.testRun, file: this.importFile});
      // Every import (real, test run or rejected) adds a history entry, so refresh it here — otherwise the
      // Verlauf tab, once opened, keeps showing a stale list.
      if (this.canSeeHistory) {
        await studyDefinitionAdminModule.loadHistory();
      }
    } finally {
      this.importing = false;
    }
  }

  async runUpdateDates() {
    this.updatingDates = true;
    try {
      const result = await studyDefinitionAdminModule.updateDates({
        studyDefinitionId: this.studyDefinitionId,
        ethicDate: this.ethicDate ?? undefined,
        goLiveDate: this.goLiveDate ?? undefined
      });
      if (result) {
        this.ethicDate = this.normalizeDate(result.ethicDate);
        this.goLiveDate = this.normalizeDate(result.goLiveDate);
        this.showDatesUpdated = true;
      }
    } finally {
      this.updatingDates = false;
    }
  }

  get guides() {
    return adminGuides;
  }

  openGuide(guide: { link: string }) {
    window.open(guide.link, "_blank");
  }

}
